import { useEffect, useState } from "react";
import {
  Box,
  HStack,
  RippleButton,
  VStack,
  Text,
  Icon,
  Spinner,
  type IconName,
  Accordion,
  AccordionTrigger,
  AccordionContent,
  AccordionItem,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";

import { formatDateTime, isScoreTestLive } from "../../../../shared/utils";
import { serverNow } from "../../../../shared/serverClock";
import styles from "./TestCard.module.scss";

const IconContainer = ({
  iconName,
  iconColor,
  iconContainerColor,
}: {
  iconName: IconName;
  iconColor?: string;
  iconContainerColor?: string;
}) => {
  const { theme } = useTheme();
  return (
    <Box
      alignItems="center"
      justifyContent="center"
      width="3.75rem"
      height="3.75rem"
      borderRadius={theme.radii.xl}
      className={styles.iconContainer}
      style={{
        backgroundColor: iconContainerColor ?? theme.colors.primary[95],
      }}
    >
      <Icon
        name={iconName}
        size={28}
        color={iconColor ?? theme.colors.primary[50]}
      />
    </Box>
  );
};

export const TestCard = ({
  children,
  backgroundColor,
  className,
}: {
  children: React.ReactNode;
  backgroundColor?: string;
  className?: string;
}) => {
  const { theme } = useTheme();
  return (
    <VStack
      gap="s"
      p="m"
      py={theme.spacing.lg}
      width="100%"
      className={className}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {children}
    </VStack>
  );
};

export const TestCardHeader = ({
  iconName = "book",
  iconColor,
  iconContainerColor,
  alignItems = "center",
  children,
  ...props
}: {
  iconName?: IconName;
  iconColor?: string;
  iconContainerColor?: string;
  alignItems?: "center" | "flex-start";
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof HStack>, "children" | "alignItems">) => {
  return (
    <HStack gap="m" alignItems={alignItems} {...props}>
      <IconContainer
        iconName={iconName}
        iconColor={iconColor}
        iconContainerColor={iconContainerColor}
      />
      {children}
    </HStack>
  );
};

export const TestCardBody = ({
  children,
  gap = "sm",
}: {
  children: React.ReactNode;
  gap?: React.ComponentProps<typeof VStack>["gap"];
}) => {
  return (
    <HStack gap="m">
      <Box width="3.75rem" className={styles.iconSpacer} />
      <VStack gap={gap} flex={1} className={styles.flexMin}>
        {children}
      </VStack>
    </HStack>
  );
};

export const TestCardGroup = ({
  title,
  iconName = "book",
  iconColor,
  iconContainerColor,
  badge,
  action,
  children,
}: {
  title: string;
  iconName?: IconName;
  iconColor?: string;
  iconContainerColor?: string;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <Accordion defaultValue={["group"]}>
      <AccordionItem value="group" className={styles.groupItem}>
        <AccordionTrigger>
          <TestCardHeader
            iconName={iconName}
            iconColor={iconColor}
            iconContainerColor={iconContainerColor}
            align="center"
            // style={{ width: "100%", minWidth: 0 }}
          >
            <HStack
              gap="sm"
              alignItems="center"
              style={{ minWidth: 0, flex: 1 }}
            >
              <VStack gap="xs" style={{ minWidth: 0, flex: 1 }}>
                <TestCardTitle text={title} />
                {badge}
              </VStack>
              {action}
            </HStack>
          </TestCardHeader>
        </AccordionTrigger>
        <AccordionContent>
          <VStack gap="md">{children}</VStack>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export const TestCardAction = ({
  children,
  gap = "md",
}: {
  children: React.ReactNode;
  gap?: React.ComponentProps<typeof HStack>["gap"];
}) => {
  return (
    <HStack gap={gap} justifyContent="flex-end">
      {children}
    </HStack>
  );
};

export const TestCardButton = ({
  text,
  onPress,
  disabled,
  variant = "outline",
  leftIconName,
  compact = false,
  isLoading,
  tabularNums,
  customLabel,
  buttonRef,
  height = 44,
  backgroundColor,
}: {
  text: string;
  onPress: () => void;
  disabled?: boolean;
  variant?: "outline" | "primary" | "danger";
  leftIconName?: IconName;
  compact?: boolean;
  isLoading?: boolean;
  tabularNums?: boolean;
  customLabel?: React.ReactNode;
  buttonRef?: React.Ref<HTMLElement>;
  height?: number;
  backgroundColor?: string;
}) => {
  const { theme } = useTheme();
  const labelColor = disabled
    ? theme.colors.neutral[50]
    : variant === "primary"
      ? theme.colors.neutral[100]
      : variant === "danger"
        ? theme.colors.error[60]
        : theme.colors.primary[40];

  return (
    <RippleButton
      ref={buttonRef}
      height={height}
      size="sm"
      onPress={onPress}
      variant={variant === "primary" ? "primary" : "outline"}
      color={variant === "danger" ? "error" : "primary"}
      shape="round"
      disabled={disabled || isLoading}
      borderColor={variant === "danger" ? theme.colors.error[60] : undefined}
      backgroundColor={backgroundColor}
      labelColor={labelColor}
      className={`${styles.cardButton} ${compact ? styles.cardButtonCompact : ""} ${tabularNums ? styles.cardButtonTabular : ""}`}
    >
      <Box className={styles.cardButtonContent}>
        <HStack
          gap="sm"
          alignItems="center"
          justifyContent="center"
          className={isLoading ? styles.cardButtonContentLoading : undefined}
        >
          {leftIconName && (
            <Icon name={leftIconName} size={16} color={labelColor} />
          )}
          {customLabel ?? text}
        </HStack>
        {isLoading && (
          <Box className={styles.cardButtonSpinner}>
            <Spinner size="sm" color={labelColor} />
          </Box>
        )}
      </Box>
    </RippleButton>
  );
};

export const TestCardTitle = ({ text }: { text: string }) => {
  return (
    <Text variant="body" size="medium" weight="emphasized" ellipsis>
      {text}
    </Text>
  );
};

export const PaperTitle = ({ text }: { text: string }) => {
  const { theme } = useTheme();
  return (
    <Text variant="body" color={theme.colors.neutral[30]} ellipsis>
      {text}
    </Text>
  );
};

export const TestCardDuration = ({ duration }: { duration: number }) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");

  return (
    <HStack gap="xs" alignItems="center">
      <Icon name="clock" size={14} color={theme.colors.neutral[40]} />
      <Text
        variant="body"
        size="small"
        color={theme.colors.neutral[40]}
        className={styles.dateRangeText}
      >
        {t("minutes", { count: duration })}
      </Text>
    </HStack>
  );
};

export const TestCardScore = ({
  score,
}: {
  score: { obtained: number; total: number };
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");
  return (
    <Text variant="body" size="small" color={theme.colors.neutral[40]}>
      {t("score")}{" "}
      <Text
        variant="body"
        size="small"
        color={theme.colors.test[50]}
        style={{ display: "inline" }}
      >
        {score.obtained}
      </Text>
      /{score.total}
    </Text>
  );
};

export const TestCardQuestionCount = ({ count }: { count: number }) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");

  return (
    <HStack gap="xs" alignItems="center">
      <Icon name="quiz" size={14} color={theme.colors.neutral[40]} />
      <Text
        variant="body"
        size="small"
        color={theme.colors.neutral[40]}
        className={styles.dateRangeText}
      >
        {t("questions_count", { count })}
      </Text>
    </HStack>
  );
};

export const TestCardDateRange = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const { theme } = useTheme();

  return (
    <HStack className={styles.dateRangeContainer}>
      <HStack gap="xs" alignItems="center" style={{ flexShrink: 0 }}>
        <span className={styles.dateRangeIcon}>
          <Icon name="calendar" size={16} color={theme.colors.neutral[40]} />
        </span>
        <Text
          variant="body"
          size="small"
          color={theme.colors.neutral[40]}
          className={styles.dateRangeText}
        >
          {formatDateTime(startDate)} -
        </Text>
      </HStack>
      <Text
        variant="body"
        size="small"
        color={theme.colors.neutral[40]}
        className={styles.dateRangeText}
      >
        {formatDateTime(endDate)}
      </Text>
    </HStack>
  );
};

export const StatusTag = ({
  status,
  startDate,
  endDate,
}: {
  status: string;
  startDate?: string;
  endDate?: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");

  const [, setNowTick] = useState(() => {
    try {
      return serverNow();
    } catch {
      return Date.now();
    }
  });
  useEffect(() => {
    if (status !== "registered" || !startDate || !endDate) return;
    const timer = setInterval(() => {
      try {
        setNowTick(serverNow());
      } catch {
        setNowTick(Date.now());
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [status, startDate, endDate]);

  const isLive =
    status === "in_progress" ||
    (status === "registered" && isScoreTestLive(startDate, endDate));

  const statusConfig: Record<string, { color: string; labelKey: string }> = {
    unregistered: {
      color: theme.colors.tertiary[40],
      labelKey: "unregistered",
    },
    registered: {
      color: theme.colors.success?.[50],
      labelKey: "registered",
    },
    in_progress: {
      color: theme.colors.error[60],
      labelKey: "live",
    },
    attempted: {
      color: theme.colors.success?.[50],
      labelKey: "complete",
    },
    missed: {
      color: theme.colors.error?.[50],
      labelKey: "missed_status",
    },
  };

  const config = statusConfig[status] ?? statusConfig.unregistered;

  // Override label to "live" when the test is live (in time range)
  const labelKey = isLive ? "live" : config.labelKey;
  const color = isLive ? theme.colors.error[60] : config.color;

  return (
    <Box
      className={`${styles.statusTag} ${isLive ? styles.liveBadge : ""}`}
      style={{ borderColor: color }}
    >
      {isLive && (
        <Box className={styles.liveDot} style={{ backgroundColor: color }} />
      )}
      <Text variant="label" size="small" weight="regular" color={color}>
        {t(labelKey)}
      </Text>
    </Box>
  );
};
